/**
 * WordPressカスタムブロック用のRiveアニメーション埋め込みスクリプト。
 * @module RiveEmbed
 */
import { Rive, Layout, Fit } from '@rive-app/canvas-lite';

/**
 * 共通のディレクトリパスを取得します。
 * @returns {string} ベースディレクトリパス。
 */
const getBasePath = () => {
  const baseDirectory = 'wp-content/themes/sng100th/assets/rive/';
  const isLocal =
    window.location.hostname.startsWith('192.168.') ||
    window.location.hostname === '127.0.0.1';
  return isLocal ? `/${baseDirectory}` : `/cms/${baseDirectory}`;
};

/**
 * 指定されたキャンバスにRiveアニメーションインスタンスをロードし、初期化します。
 * @param {HTMLElement} canvas - Riveアニメーションをレンダリングするキャンバス要素。
 * @param {string} fileName - ロードするRiveファイルの名前。
 * @param {string} basePath - Riveファイルが格納されているベースパス。
 * @param {Function} onLoadAdditional - Riveファイルがロードされたときに呼び出される追加のコールバック関数。
 * @returns {Rive} ロードされたRiveインスタンス。
 */
const loadRiveInstance = (canvas, fileName, basePath, onLoadAdditional) => {
  const filePath = `${basePath}${fileName}.riv`;
  const autoplay = fileName !== 'top_opening'; //オープニングrivだけ自動再生無効

  const riveInstance = new Rive({
    src: filePath,
    canvas: canvas, // アニメーションがレンダリングされるキャンバス要素。
    autoplay: autoplay,
    layout: new Layout({ fit: Fit.Cover }),
    onLoad: () => {
      // アニメーションがロードされたときに、描画面をキャンバスのサイズに合わせてリサイズ
      riveInstance.resizeDrawingSurfaceToCanvas();
      // ロードが完了したら、クラスを追加する
      canvas.classList.add('is-loaded');
      //追加で処理をしたい場合
      onLoadAdditional(riveInstance, canvas);
      // グローバルオブジェクトにインスタンスを保存
      window.riveInstances[fileName] = riveInstance;
    },
    // エラーハンドリングを追加
    onError: (error) => {
      console.error('Rive loading error:', error);
    },
  });

  return riveInstance;
};

/**
 * ページ内の指定されたコンテナにRiveアニメーションを埋め込みます。
 * @param {Function} [onLoadAdditional=() => {}] - Riveファイルがロードされたときに呼び出される追加のコールバック関数。
 * @returns {Array<Rive>} ロードされたRiveインスタンスの配列。
 */

export function riveEmbed(onLoadAdditional = () => { }) {
  return new Promise((resolve, reject) => {
    const basePath = getBasePath();
    window.riveInstances = {};

    // .js-rive-containerがページ内に存在しない場合、処理を中止
    const containers = document.querySelectorAll(
      '.js-rive-container:not(.is-initialized)'
    );
    if (containers.length === 0) {
      resolve([]);
      return;
    }

    const loadPromises = [];

    containers.forEach((container) => {
      container.classList.add('is-initialized');
      const canvases = container.querySelectorAll(
        '.js-rive-canvas:not(.is-loaded)'
      );

      canvases.forEach((canvas) => {
        const fileName = canvas.dataset.riveName;
        // Promiseを生成して、loadPromises配列に追加
        const loadPromise = new Promise(
          (resolveCanvas, rejectCanvas) => {
            const riveInstance = loadRiveInstance(
              canvas,
              fileName,
              basePath,
              (instance) => {
                onLoadAdditional(instance, canvas);
                resolveCanvas(); // このキャンバスのロードが完了したことを示す
              }
            );
            window.riveInstances[fileName] = riveInstance;
          }
        );
        loadPromises.push(loadPromise);
      });
    });

    // すべてのRiveアニメーションのロードが完了したら、Promiseを解決
    Promise.all(loadPromises)
      .then(() => {
        resolve(window.riveInstances);
      })
      .catch(reject);

    // resizeイベントリスナーは、Promiseの解決に影響しないため、そのままにしておく
    window.addEventListener(
      'resize',
      () => {
        Object.values(window.riveInstances).forEach((instance) =>
          instance.resizeDrawingSurfaceToCanvas()
        );
      },
      false
    );
  });
}
